import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';

const emptyForm = { name:'', contact_person:'', phone:'', note:'', product_ids:[] };

// Постачальники — мережеві (спільні на всю мережу). Замовлення формується на обрану
// точку (її адреса йде в повідомлення).
export default function SuppliersPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [tab, setTab] = useState('order');          // 'order' | 'manage'
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);     // весь мережевий каталог
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');       // точка доставки для замовлення
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [prodSearch, setProdSearch] = useState('');

  const [qty, setQty] = useState({});
  const [orders, setOrders] = useState(null);
  const [copied, setCopied] = useState(null);

  const canEdit = isSuperAdmin;
  const store = stores.find(s => s.id === storeId);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    const [sup, prods, st] = await Promise.all([
      api.get('/suppliers/').then(r => r.data).catch(() => []),
      api.get('/catalog/products').then(r => r.data).catch(() => []),
      api.get('/stores/').then(r => r.data).catch(() => []),
    ]);
    setSuppliers(sup);
    setProducts(prods);
    setStores(st);
    if (st.length && !storeId) setStoreId(st[0].id);
    setLoading(false);
  }

  // ── керування постачальниками ──
  function startAdd() { setForm(emptyForm); setEditingId(null); setShowForm(true); setProdSearch(''); }
  function startEdit(s) {
    setForm({ name:s.name, contact_person:s.contact_person||'', phone:s.phone||'', note:s.note||'', product_ids:s.product_ids||[] });
    setEditingId(s.id); setShowForm(true); setProdSearch('');
  }
  function toggleProduct(pid) {
    setForm(f => ({ ...f, product_ids: f.product_ids.includes(pid)
      ? f.product_ids.filter(x => x !== pid) : [...f.product_ids, pid] }));
  }
  async function saveSupplier(e) {
    e.preventDefault();
    if (editingId) await api.patch(`/suppliers/${editingId}`, form);
    else await api.post('/suppliers/', form);
    setShowForm(false); setForm(emptyForm); setEditingId(null);
    await load();
  }
  async function deleteSupplier(id) {
    if (!window.confirm('Видалити постачальника?')) return;
    await api.delete(`/suppliers/${id}`);
    await load();
  }

  // ── формування замовлення ──
  function setQ(pid, v) { setQty(q => ({ ...q, [pid]: v })); }
  function buildOrders() {
    const address = store?.address || '—';
    const result = [];
    for (const s of suppliers) {
      const lines = (s.products || [])
        .filter(p => parseFloat(qty[p.id]) > 0)
        .map(p => `${p.name} *${qty[p.id]}`);
      if (!lines.length) continue;
      const msg = `Доброго дня! Нам потрібно:\n${lines.join('\n')}\nНа адресу: ${address}`;
      result.push({ supplier: s, message: msg });
    }
    setOrders(result);
    setCopied(null);
  }
  async function copyMsg(i, text) {
    try { await navigator.clipboard.writeText(text); setCopied(i); setTimeout(() => setCopied(null), 2000); }
    catch { /* no-op */ }
  }

  const visibleProducts = products.filter(p => p.name.toLowerCase().includes(prodSearch.toLowerCase()));

  return (
    <AppShell title="📦 Постачальники та замовлення" onBack={() => navigate('/dashboard')}>
      <div className="row" style={{ gap: 8, marginBottom: 18 }}>
        <button className={tab==='order'?'btn btn-primary btn-sm':'btn btn-soft btn-sm'} onClick={() => { setTab('order'); setOrders(null); }}>🧾 Сформувати замовлення</button>
        {canEdit && <button className={tab==='manage'?'btn btn-primary btn-sm':'btn btn-soft btn-sm'} onClick={() => setTab('manage')}>⚙️ Постачальники</button>}
      </div>

      {loading ? <p className="hint">Завантаження…</p> : tab === 'manage' && canEdit ? (
        // ─────────── керування ───────────
        <div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 14 }} onClick={startAdd}>+ Постачальник</button>
          {showForm && (
            <form onSubmit={saveSupplier} className="card" style={{ marginBottom: 18, display:'flex', flexDirection:'column', gap:12 }}>
              <h3 style={{ margin:0, color:'#f5a623' }}>{editingId ? 'Редагувати постачальника' : 'Новий постачальник'}</h3>
              <div className="field"><label className="label">Назва</label>
                <input value={form.name} required onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="напр. Пивна компанія" /></div>
              <div className="row" style={{ gap:12 }}>
                <div className="field" style={{ flex:1 }}><label className="label">Контактна особа</label>
                  <input value={form.contact_person} onChange={e => setForm(f=>({...f,contact_person:e.target.value}))} placeholder="Імʼя" /></div>
                <div className="field" style={{ flex:1 }}><label className="label">Телефон</label>
                  <input value={form.phone} onChange={e => setForm(f=>({...f,phone:e.target.value}))} placeholder="+380…" /></div>
              </div>
              <div className="field"><label className="label">Нотатка</label>
                <input value={form.note} onChange={e => setForm(f=>({...f,note:e.target.value}))} placeholder="напр. графік поставок" /></div>
              <div className="field">
                <label className="label">Які товари постачає ({form.product_ids.length})</label>
                <input value={prodSearch} onChange={e => setProdSearch(e.target.value)} placeholder="🔍 пошук товару…" style={{ marginBottom:8 }} />
                <div style={styles.prodList}>
                  {visibleProducts.map(p => (
                    <label key={p.id} style={styles.prodItem}>
                      <input type="checkbox" checked={form.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                      <span>{p.name}</span>
                    </label>
                  ))}
                  {visibleProducts.length === 0 && <p className="muted" style={{ padding:8 }}>Нічого не знайдено</p>}
                </div>
              </div>
              <div className="row" style={{ gap:10 }}>
                <button className="btn btn-primary" type="submit">{editingId ? 'Зберегти' : 'Додати'}</button>
                <button className="btn btn-soft" type="button" onClick={() => { setShowForm(false); setEditingId(null); }}>Скасувати</button>
              </div>
            </form>
          )}

          {suppliers.length === 0 ? <p className="hint">Постачальників ще немає — додайте першого.</p> : (
            <div className="grid grid-auto">
              {suppliers.map(s => (
                <div key={s.id} className="card">
                  <div className="row-between">
                    <h3 style={{ margin:0 }}>{s.name}</h3>
                    <div className="row" style={{ gap:6 }}>
                      <button className="btn btn-soft btn-sm" onClick={() => startEdit(s)}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteSupplier(s.id)}>🗑</button>
                    </div>
                  </div>
                  <p className="muted" style={{ margin:'6px 0', fontSize:13 }}>
                    {s.contact_person ? s.contact_person + ' · ' : ''}{s.phone || 'без телефону'}
                  </p>
                  <p className="muted" style={{ fontSize:12 }}>Товарів: {s.products?.length || 0}{s.note ? ' · ' + s.note : ''}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // ─────────── формування замовлення ───────────
        <div>
          {suppliers.length === 0 ? (
            <p className="hint">Спершу додайте постачальників{canEdit ? ' (вкладка «Постачальники»)' : ''} та відмітьте їхні товари.</p>
          ) : (
            <>
              <div className="field" style={{ maxWidth: 360, marginBottom: 14 }}>
                <label className="label">Доставка в магазин</label>
                <select value={storeId} onChange={e => { setStoreId(e.target.value); setOrders(null); }}>
                  {stores.map(s => <option key={s.id} value={s.id}>{s.name}{s.address ? ' — ' + s.address : ''}</option>)}
                </select>
              </div>
              {suppliers.map(s => (
                <div key={s.id} className="card" style={{ marginBottom: 14 }}>
                  <h3 style={{ marginTop:0 }}>{s.name} <span className="muted" style={{ fontSize:13, fontWeight:400 }}>{s.phone || ''}</span></h3>
                  {(s.products || []).length === 0 ? <p className="muted">У постачальника не відмічено товарів.</p> : (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {s.products.map(p => (
                        <div key={p.id} style={styles.orderRow}>
                          <span style={{ flex:1 }}>{p.name}</span>
                          <input type="number" min="0" step="1" placeholder="0" style={styles.qtyInput}
                            value={qty[p.id] || ''} onChange={e => setQ(p.id, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <button className="btn btn-primary" onClick={buildOrders}>🧾 Сформувати замовлення</button>

              {orders && (
                <div style={{ marginTop: 20 }}>
                  <h2 className="section-title">Готові повідомлення</h2>
                  {orders.length === 0 ? <p className="hint">Нічого не вибрано — вкажи кількості вище.</p> : orders.map((o, i) => (
                    <div key={i} className="card" style={{ marginBottom: 12 }}>
                      <div className="row-between" style={{ marginBottom: 8 }}>
                        <b>{o.supplier.name} {o.supplier.phone ? '· ' + o.supplier.phone : ''}</b>
                        <button className="btn btn-soft btn-sm" onClick={() => copyMsg(i, o.message)}>
                          {copied === i ? '✓ Скопійовано' : '📋 Скопіювати'}
                        </button>
                      </div>
                      <pre style={styles.msg}>{o.message}</pre>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </AppShell>
  );
}

const styles = {
  prodList: { maxHeight: 220, overflowY: 'auto', border: '1px solid #38301f', borderRadius: 8, padding: 6, background: '#1c1812' },
  prodItem: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', fontSize: 14 },
  orderRow: { display: 'flex', alignItems: 'center', gap: 10 },
  qtyInput: { width: 80, padding: '7px 10px', borderRadius: 8, border: '1px solid #38301f', background: '#211c14', color: '#fff', textAlign: 'center' },
  msg: { whiteSpace: 'pre-wrap', fontFamily: 'inherit', background: '#1c1812', border: '1px solid #38301f', borderRadius: 8, padding: 12, margin: 0, color: '#fff', fontSize: 14 },
};
